import moment from 'moment-timezone';

const ensureIsoString = (val) => {
  if (!val) return val;
  const m = moment.utc(val);
  if (m.isValid()) {
    return m.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
  }
  return val;
};

const removeUndefinedFields = (obj) => {
  if (typeof obj !== 'object' || obj === null) {
    return obj; // Return the value if it's not an object
  }

  // Loop through the object keys
  Object.keys(obj).forEach((key) => {
    const value = obj[key];

    // Recursively clean nested objects
    if (typeof value === 'object' && value !== null) {
      removeUndefinedFields(value);
    }

    // If the value is undefined, delete the property
    if (value === undefined) {
      delete obj[key];
    }
  });

  return obj;
};

const generateJson = (vehicles, shipments) => {
  // Find the earliest shipment or vehicle start time
  const shipmentTimes = shipments
    .filter((s) => !!s.deliveryStartTime)
    .map((s) => moment.utc(s.deliveryStartTime).valueOf())
    .filter((t) => !isNaN(t));

  const vehicleTimes = vehicles
    .filter((v) => !!v.startTimeWindowStartTime)
    .map((v) => moment.utc(v.startTimeWindowStartTime).valueOf())
    .filter((t) => !isNaN(t));

  const allTimes = [...shipmentTimes, ...vehicleTimes];
  const minTime = allTimes.length > 0 ? Math.min(...allTimes) : moment.utc().valueOf();
  const minDate = moment.utc(minTime);
  const globalStartTime = minDate.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

  const globalEndTime = minDate.clone().add(2, 'days').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

  return removeUndefinedFields({
    model: {
      shipments: shipments.map((shipment) => {
        const visit = {
          loadDemands: shipment.loadDemands,
          label: shipment.label.toString(),
          pickupToDeliveryTimeLimit:
            typeof shipment.pickupToDeliveryTimeLimit === 'number'
              ? `${shipment.pickupToDeliveryTimeLimit}s`
              : shipment.pickupToDeliveryTimeLimit,
        };
        if (!!shipment.pickupArrivalWaypointLatitude) {
          visit['pickups'] = [
            {
              arrivalLocation: {
                latitude: parseFloat(shipment.pickupArrivalWaypointLatitude),
                longitude: parseFloat(shipment.pickupArrivalWaypointLongitude),
              },
              timeWindows: [
                {
                  startTime: ensureIsoString(shipment.pickupStartTime),
                  endTime: ensureIsoString(shipment.pickupEndTime),
                },
              ],
              duration: `${shipment.pickupDuration}s`,
            },
          ];
        }
        visit['deliveries'] = [
          {
            arrivalLocation: {
              latitude: parseFloat(shipment.deliveryArrivalWaypointLatitude),
              longitude: parseFloat(shipment.deliveryArrivalWaypointLongitude),
            },
            timeWindows: [
              {
                startTime: ensureIsoString(shipment.deliveryStartTime),
                endTime: ensureIsoString(shipment.deliveryEndTime),
              },
            ],
            duration: `${shipment.deliveryDuration}s`,
          },
        ];

        return visit;
      }),
      vehicles: vehicles.map((vehicle) => {
        const vehicleData = {
          travelMode: vehicle.travelMode,
          travelDurationMultiple: vehicle.travelDurationMultiple,
          startLocation: {
            latitude: vehicle.startWaypointLatitude,
            longitude: vehicle.startWaypointLongitude,
          },
          startTimeWindows: [
            {
              startTime: ensureIsoString(vehicle.startTimeWindowStartTime),
            },
          ],
          endTimeWindows: [
            {
              endTime: ensureIsoString(vehicle.endTimeWindowEndTime),
            },
          ],
          loadLimits: vehicle.loadLimits,
          label: vehicle.label.toString(),
          costPerKilometer: vehicle.costPerKilometer,
        };

        if (!!vehicle.endWaypointLatitude) {
          vehicleData.endLocation = {
            latitude: vehicle.endWaypointLatitude,
            longitude: vehicle.endWaypointLongitude,
          };
        }

        if (!!vehicle.breakStartTime && !!vehicle.breakDuration) {
          vehicleData['breakRule'] = {};
          vehicleData['breakRule']['breakRequests'] = [
            {
              earliestStartTime: ensureIsoString(vehicle.breakStartTime),
              latestStartTime: ensureIsoString(vehicle.breakStartTime),
              minDuration: `${vehicle.breakDuration}s`,
            },
          ];
          if (!!vehicle.minBreakDuration && !!vehicle.maxInterBreakDuration) {
            vehicleData['breakRule']['frequencyConstraints'] = [
              {
                minBreakDuration: `${vehicle.minBreakDuration}s`,
                maxInterBreakDuration: `${vehicle.maxInterBreakDuration}s`,
              },
            ];
          }
        }

        if (!!vehicle.fixedCost) {
          vehicleData.fixedCost = vehicle.fixedCost;
        }

        return vehicleData;
      }),
      globalStartTime: globalStartTime,
      globalEndTime: globalEndTime,
    },
    populatePolylines: true,
  });
};

export default generateJson;
